import React, { useCallback, useEffect, useRef, useState } from 'react';
import _get from 'lodash/get';
import QrScanner from 'qr-scanner';
import { useNavigate } from 'react-router-dom';
import { USER_SELECTED_CAMERA_ID } from '@lib/enums/localStorageKeys';
import useCameraPermission from '@lib/hooks/useCameraPermission';
import usePrevious from '@lib/hooks/usePrevious';
import { captureException } from '@lib/utils/captureException';
import { logger } from '@lib/services/logger';

type Args = {
  qrCodeSuccessCallback: (res: QrScanner.ScanResult) => void;
};

export type UseCameraSetupResult = {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  cameraPermission: string; // "granted", "denied", "prompt".
  cameras: QrScanner.Camera[];
  handleCameraChange: (cameraId: string) => Promise<void>;
  isReady: boolean;
  isTorchAvailable: boolean;
  selectedCameraId: string;
  toggleTorch: () => Promise<void>;
  torchEnabled: boolean;
};

const getStoredCameraId = () => {
  return localStorage.getItem(USER_SELECTED_CAMERA_ID);
};

type QrScannerOptions = {
  onDecodeError?: (error: Error | string) => void;
  calculateScanRegion?: (video: HTMLVideoElement) => QrScanner.ScanRegion;
  preferredCamera?: QrScanner.FacingMode | QrScanner.DeviceId;
  maxScansPerSecond?: number;
  highlightScanRegion?: boolean;
  highlightCodeOutline?: boolean;
  overlay?: HTMLDivElement;
  returnDetailedScanResult?: true;
};

const createQrScanner = (
  videoArg: HTMLVideoElement,
  onDecodeArg: (result: QrScanner.ScanResult) => void,
  optionsArg: QrScannerOptions,
) => {
  return new (
    QrScanner as unknown as {
      new (
        video: HTMLVideoElement,
        onDecode: (result: QrScanner.ScanResult) => void,
        options: QrScannerOptions,
      ): QrScanner;
    }
  )(videoArg, onDecodeArg, optionsArg);
};

const waitForVideoReady = (video: HTMLVideoElement) => {
  return new Promise<void>((resolve) => {
    if (video?.readyState && video.readyState >= 2) {
      resolve();
    } else if (video) {
      video.addEventListener('loadeddata', () => resolve(), { once: true });
    }
  });
};

const useCameraSetup = ({
  qrCodeSuccessCallback,
}: Args): UseCameraSetupResult => {
  const initializedRef = useRef(false);
  const navigate = useNavigate();
  const [cameras, setCameras] = useState<QrScanner.Camera[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState(
    getStoredCameraId() || '',
  );

  const [isReady, setReady] = useState(false);
  const [isTorchAvailable, setIsTorchAvailable] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);

  const qrScannerRef = useRef<QrScanner | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const cameraPermission = useCameraPermission({
    initialized: initializedRef.current,
    selectedCameraId,
  });
  const prevCameraPermission = usePrevious(cameraPermission);

  const selectCameraOption = useCallback(() => {
    try {
      const stream = videoRef.current?.srcObject as MediaStream;
      const activeCamera = stream?.getVideoTracks()[0];
      if (activeCamera) {
        const activeCameraSettings = activeCamera.getSettings();
        const activeCameraId = activeCameraSettings?.deviceId;
        if (activeCameraId) setSelectedCameraId(activeCameraId);
      }
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }, []);

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
      tracks.forEach((track) => {
        track.stop();
        // eslint-disable-next-line no-param-reassign
        track.enabled = false;
      });
      videoRef.current.srcObject = null;
    }
  };

  const handleCameraChange = useCallback(
    async (cameraId: string) => {
      setReady(false);
      setTorchEnabled(false);
      setIsTorchAvailable(false);

      try {
        if (qrScannerRef.current) {
          logger.debug('Scanner: camera - switching camera', { cameraId });
          stopCamera();
          const scanner = qrScannerRef.current;
          scanner.stop();
          await scanner.turnFlashOff();
          await scanner.setCamera(cameraId);
          await scanner.start();
          const hasFlash = (await scanner.hasFlash()) || false;
          setIsTorchAvailable(hasFlash);
          setSelectedCameraId(cameraId);
          setTorchEnabled(false);
          localStorage.setItem(USER_SELECTED_CAMERA_ID, cameraId);
          selectCameraOption();
          logger.info('Scanner: camera - switched successfully', {
            cameraId,
            hasFlash,
          });
        }
      } catch (error) {
        logger.error('Scanner: camera - switch failed', {
          cameraId,
          error: error instanceof Error ? error.message : String(error),
        });
        captureException(
          error instanceof Error ? error : new Error(String(error)),
        );
      } finally {
        setReady(true);
      }
    },
    [selectCameraOption],
  );

  const toggleTorch = useCallback(async () => {
    if (!qrScannerRef.current) return;

    try {
      await qrScannerRef.current.toggleFlash();
      setTorchEnabled(qrScannerRef.current?.isFlashOn());
    } catch (error) {
      logger.error('Scanner: torch - toggle failed', {
        error: error instanceof Error ? error.message : String(error),
      });
      // Some Android camera drivers advertise torch support but reject it
      // (e.g. "UnknownError: setPhotoOptions failed"), so hide the toggle
      // instead of letting it fail on every tap.
      setIsTorchAvailable(false);
      setTorchEnabled(false);
      // DOMExceptions here come from the device/driver and aren't actionable.
      if (!(error instanceof DOMException)) {
        captureException(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    }
  }, []);

  useEffect(() => {
    if (initializedRef.current) return () => undefined;
    initializedRef.current = true;
    const initializeCamera = async () => {
      if (!videoRef.current) return;

      try {
        const prevCameraId = getStoredCameraId();
        qrScannerRef.current = createQrScanner(
          videoRef.current,
          qrCodeSuccessCallback,
          {
            highlightCodeOutline: true,
            highlightScanRegion: true,
            returnDetailedScanResult: true,
            preferredCamera: prevCameraId || 'environment',
            maxScansPerSecond: 5,
            onDecodeError: (error) => {
              if (
                typeof error === 'string' &&
                error.includes(QrScanner.NO_QR_CODE_FOUND)
              )
                return;
              captureException(
                typeof error === 'string' ? new Error(error) : error,
              );
            },
            calculateScanRegion: (videoProp) => {
              if (videoProp.videoWidth === 0 || videoProp.videoHeight === 0) {
                return {};
              }
              const smallestDimension = Math.min(
                videoProp.videoWidth,
                videoProp.videoHeight,
              );
              const scanRegionSize = Math.round(smallestDimension / 3);
              return {
                x: (videoProp.videoWidth - scanRegionSize) / 2,
                y: (videoProp.videoHeight - scanRegionSize) / 2,
                width: scanRegionSize,
                height: scanRegionSize,
              };
            },
          },
        );

        const listCameras = await QrScanner.listCameras(true);
        setCameras(listCameras);

        await qrScannerRef.current.start();
        await waitForVideoReady(videoRef.current ?? null);

        if (qrScannerRef.current) {
          const hasFlash = await qrScannerRef.current.hasFlash();
          setReady(true);
          setIsTorchAvailable(hasFlash);
          setTorchEnabled(qrScannerRef.current?.isFlashOn());
          selectCameraOption();
          logger.info('Scanner: camera - initialized', {
            camerasCount: listCameras.length,
            hasFlash,
            preferredCamera: prevCameraId || 'environment',
          });
        }
      } catch (error) {
        logger.error('Scanner: camera - initialization failed', {
          error: typeof error === 'string' ? error : (error as Error)?.message,
        });
        if (typeof error === 'string') {
          captureException(new Error(error));
        } else if (error instanceof Error) {
          captureException(error);
        }
        setReady(true);
      }
    };

    initializeCamera();

    return () => undefined;
  }, [qrCodeSuccessCallback, selectCameraOption]);

  useEffect(() => {
    return () => {
      if (_get(qrScannerRef.current, '_active')) {
        qrScannerRef.current?.stop();
        qrScannerRef.current?.destroy();
        qrScannerRef.current = null;
      }
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (
      (prevCameraPermission === 'granted' ||
        prevCameraPermission === 'denied') &&
      cameraPermission === 'prompt'
    ) {
      navigate(0);
    }
  }, [cameraPermission, navigate, prevCameraPermission]);

  return {
    videoRef,
    cameraPermission,
    cameras,
    handleCameraChange,
    isReady,
    isTorchAvailable,
    selectedCameraId,
    toggleTorch,
    torchEnabled,
  };
};

export default useCameraSetup;
